import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { DriverDetail } from '@paquery-team/lib-driver-detail';
import rest, { getData } from 'util/Api';
import { useSelector, useDispatch } from 'react-redux';
import {
  countriesGlobalsSelector,
  vehicleTypesGlobalsSelector,
} from 'redux/globals/selectors';
import { IMAGE_REGEX } from 'constants/defaultValues';
import { PERMISSIONS, hasPermission } from 'constants/roles';
import { authProfile } from 'redux/auth/selectors';
import {
  paquerviewAvatarSelector,
  paquerviewLoadingSelector,
} from 'redux/paquerView/selectors';
import { actions } from 'redux/paquerView/slice';
import { actions as paquerListingActions } from 'redux/paquerList/slice';
import { actions as ppointDepartureActions } from 'redux/packagePaquerypointDeparture/slice';
import usePaquerview from 'redux/paquerView';
import { notification } from 'antd';
import { ContainerOutlined, HistoryOutlined } from '@ant-design/icons';
import API from 'constants/api';
import * as Yup from 'yup';
import { useZones } from 'services/ZoneService';
import usePackagePpointDeparture from 'redux/packagePaquerypointDeparture';
import { packagePaquerypointDepartureAllPaqueryPointsItemsSelector } from 'redux/packagePaquerypointDeparture/selectors';
import { getById } from 'services/DriverService';
import hydrateCatalogItems from 'util/catalogItems';
import PaquerPackages from './components/packages';
import PaquerHistory from './components/history';
import usePaquers from 'redux/paquerList';

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .matches(/^[\p{L}\p{N}\s'-]+$/u, 'No se permiten caracteres especiales')
    .required('Es necesario indicar el nombre del paquer'),
  lastName: Yup.string()
    .matches(/^[\p{L}\p{N}\s'-]+$/u, 'No se permiten caracteres especiales')
    .required('Es necesario indicar el apellido del paquer'),
  docNumber: Yup.number()
    .typeError('Solo se deben indicar números y es requerido')
    .required('El número de documento es necesario'),
  mobile: Yup.number()
    .typeError('Solo deben ser numeros y son requeridos')
    .required('Es necesario indicar el número de teléfono'),
  email: Yup.string()
    .email('El formato de correo no es correcto')
    .required('Es necesario indicar el correo electrónico'),
  city: Yup.object().shape({
    id: Yup.number()
      .typeError('Es necesario indicar la ciudad')
      .required('Es necesario indicar la ciudad'),
    countryID: Yup.number()
      .typeError('Es necesario indicar el país')
      .required('Es necesario indicar el país'),
  }),
});

const initialValues = {
  name: '',
  lastName: '',
  docNumber: null,
  cbu: null,
  cuil: null,
  pwd: '',
  birthDate: null,
  prefix: '',
  phone: null,
  active: true,
  confirmpassword: '',
  cityID: null,
  countryID: null,
  vehicleTypeID: null,
  zones: [],
  paqueryPoints: [],
};

/** Sólo lectura: es lo que ve un rol sin permiso de edición de paquers. */
const readOnlyInputs = {
  avatar: true,
  name: true,
  lastName: true,
  birthDate: true,
  docNumber: true,
  cbu: true,
  cuil: true,
  vehicleTypeID: true,
  mobile: true,
  countryId: true,
  cityId: true,
  email: true,
  paqueryPoints: true,
  workingZones: true,
};

/** Edición: el email sigue siendo la identidad del paquer, no se toca. */
const editableInputs = {
  email: true,
};

/**
 * Returns if the paquer is active by its paquer.status property
 * @param {number} status
 * */
const isPaquerActive = (status) => {
  if (status === 1) return true;
  if (status === 6) return false;
  return false;
};

const PaquerModify = () => {
  usePaquerview();
  usePackagePpointDeparture();
  usePaquers();
  const dispatch = useDispatch();
  const history = useHistory();
  const profile = useSelector(authProfile);
  const canEdit = hasPermission(profile, PERMISSIONS.AddEditPaquer);
  const loading = useSelector(paquerviewLoadingSelector);
  const avatar = useSelector(paquerviewAvatarSelector);
  const {
    zones: { data: zones },
  } = useZones();
  const vehicleTypes = useSelector(vehicleTypesGlobalsSelector);
  const paqueryPoints = useSelector(
    packagePaquerypointDepartureAllPaqueryPointsItemsSelector,
  );
  const countries = useSelector(countriesGlobalsSelector);
  const [paquerData, setPaquerData] = useState(null);
  const [currentCity, setCurrentCity] = useState(null);
  const [cities, setCities] = useState([]);
  const { paquerId } = useParams();

  useEffect(() => {
    dispatch(actions.reset());
    dispatch(ppointDepartureActions.initial());
    dispatch(actions.setDriverId(paquerId));
  }, [dispatch, paquerId]);

  const onChangeCountry = useCallback(
    async (country) => {
      dispatch(actions.loading());
      const citiesResponse = await getData(`${API.globals.cities}/${country}`);
      setCities(citiesResponse);
      if (citiesResponse.length > 0) {
        setCurrentCity(citiesResponse[0].id);
      } else {
        setCurrentCity(null);
      }
      dispatch(actions.loaded());
    },
    [dispatch],
  );

  const onChangePassword = async (email) => {
    try {
      const body = {
        email,
      };
      const bodyParsed = new URLSearchParams(body);
      const options = {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      };
      await rest.post(
        `${API.user.changePassword}`,
        bodyParsed.toString(),
        options,
      );
    } catch (error) {
      notification.error({
        message: 'Ocurrio un problema',
        description: `Hubo un error al solicitar el cambio de contraseña${
          error.message ? `: ${error.message}` : '.'
        }`,
      });
    }
  };

  const handleGetPaquer = useCallback(async () => {
    dispatch(actions.loading());
    const fetchedPaquer = await getById(paquerId);
    setPaquerData({
      ...fetchedPaquer,
      lastName: fetchedPaquer.lastName,
      isActive: isPaquerActive(fetchedPaquer.status),
      cityID: fetchedPaquer.city.id,
      countryID: fetchedPaquer.city.countryID,
    });

    dispatch(actions.setAvatar({ data: fetchedPaquer.avatar }));

    if (fetchedPaquer.city.countryID) {
      const citiesResponse = await getData(
        `${API.globals.cities}/${fetchedPaquer.city.countryID}`,
      );
      setCities(citiesResponse);
    }

    dispatch(actions.loaded());
  }, [paquerId]);

  useEffect(() => {
    handleGetPaquer();
  }, [handleGetPaquer]);

  const driverValues = useMemo(
    () =>
      paquerData
        ? {
            ...paquerData,
            zones: hydrateCatalogItems(paquerData.zones, zones, 'fullName'),
            paqueryPoints: hydrateCatalogItems(
              paquerData.paqueryPoints,
              paqueryPoints,
              'detail',
            ),
          }
        : initialValues,
    [paquerData, zones, paqueryPoints],
  );

  const handleSubmit = async (values) => {
    const payload = {
      // status === 1 -> activo, status === 6 -> inactivo
      status: values.isActive === true ? 1 : 6,
      avatar: avatar?.name ? avatar.data.replace(IMAGE_REGEX, '') : undefined,
      birthDate: values.birthDate,
      cityID: values.city.id,
      countryId: values.city.countryID,
      vehicleTypeID: values.vehicleTypeID,
      cbu: values.cbu,
      cuil: values.cuil,
      docNumber: values.docNumber,
      email: values.email,
      lastName: values.lastName,
      mobile: values.mobile,
      name: values.name,
      zones: values.zones.map((zone) => ({ id: zone.id })),
      paqueryPoints: values.paqueryPoints.map((ppoint) => ({ id: ppoint.id })),
      logisticOperators: values.logisticOperators,
      vehicleID: values.vehicleID,
      vehicleType: values.vehicleType,
    };
    try {
      await rest.put(`${API.paquer.update}${values.id}`, payload);
      dispatch(paquerListingActions.refreshPage());
      history.push('/paquers');
    } catch (error) {
      notification.error({
        message: 'No se pudo guardar el paquer',
        description: error.message ?? 'Intente nuevamente.',
      });
    }
  };

  return (
    <DriverDetail
      loaded={!loading}
      initialValues={driverValues}
      validationSchema={validationSchema}
      disabledInputs={canEdit ? editableInputs : readOnlyInputs}
      onSubmit={canEdit ? handleSubmit : undefined}
      currentCity={currentCity}
      vehicles={vehicleTypes}
      countries={countries}
      cities={cities}
      zones={zones}
      paqueryPoints={paqueryPoints}
      onChangePassword={onChangePassword}
      onChangeCountry={onChangeCountry}
      lists={[
        {
          title: 'En tránsito',
          icon: <ContainerOutlined />,
          table: () => (
            <PaquerPackages paquerId={paquerData?.id} driver={paquerData} />
          ),
        },
        {
          title: 'Histórico',
          icon: <HistoryOutlined />,
          table: () => <PaquerHistory paquerId={paquerData?.id} />,
        },
      ]}
      isModify
    />
  );
};
export default PaquerModify;
